import { create } from 'zustand';
import type { TelemetryPoint } from '@monitor/shared';

interface SensorRealtimeState {
  latest: TelemetryPoint | null;
  history: TelemetryPoint[];
}

interface SiteRealtimeState {
  bySensor: Record<number, SensorRealtimeState>;
  lastUpdatedAt: string | null;
}

interface RealtimeState {
  bySite: Record<number, SiteRealtimeState>;
  push: (siteId: number, point: TelemetryPoint) => void;
  pushForSensor: (siteId: number, sensorId: number, point: TelemetryPoint) => void;
  // Backfill a sensor's history from the backend (older points) WITHOUT
  // disturbing `latest` (which realtime owns). Merges by time, keeps newest N.
  seedSensorHistory: (siteId: number, sensorId: number, points: TelemetryPoint[]) => void;
  reset: (siteId: number) => void;
}

// Keep ~2 hours of per-sensor history (telemetry arrives ~every 3 s → ~2400
// points) so the dashboard's realtime chart can show the wider tick windows
// (up to 1-hour gridlines) instead of collapsing to the last ~3 minutes.
const HISTORY_LIMIT = 2400;

// A point is "live" only if its measurement timestamp is within this window of
// now. When a board reconnects after an outage it flushes its store-and-forward
// queue — hundreds of points carrying their ORIGINAL (old) timestamps arrive in
// a burst. Those are real history (persisted server-side, shown in reports) but
// must NOT drive the live store, or the current KPI races through the whole
// backlog ("ตัวเลขวิ่งรัวๆ") and the live chart zig-zags. 2 minutes tolerates
// normal cadence + clock skew while cleanly excluding an outage backlog.
const LIVE_WINDOW_MS = 120_000;

export const useRealtimeStore = create<RealtimeState>((set) => ({
  bySite: {},

  // Legacy entry point - figures out sensor from the point itself.
  push: (siteId, point) => {
    const sensorId = point.sensorId ?? point.deviceId ?? 0;
    set((state) => updateState(state, siteId, sensorId, point));
  },

  pushForSensor: (siteId, sensorId, point) =>
    set((state) => updateState(state, siteId, sensorId, point)),

  seedSensorHistory: (siteId, sensorId, points) =>
    set((state) => {
      if (points.length === 0) return {};
      const prevSite = state.bySite[siteId] ?? { bySensor: {}, lastUpdatedAt: null };
      const prev = prevSite.bySensor[sensorId] ?? { latest: null, history: [] };
      // Merge by timestamp; existing (realtime) points win over backfill so a
      // coarse historical bucket never overwrites a precise live reading.
      const byTime = new Map<string, TelemetryPoint>();
      for (const p of points) byTime.set(p.time, p);
      for (const p of prev.history) byTime.set(p.time, p);
      const history = [...byTime.values()]
        .sort((a, b) => a.time.localeCompare(b.time))
        .slice(-HISTORY_LIMIT);
      // Keep the existing live `latest` unless a seed point is actually newer.
      const newest = history[history.length - 1] ?? null;
      const latest =
        prev.latest && newest && prev.latest.time >= newest.time ? prev.latest : newest;
      return {
        bySite: {
          ...state.bySite,
          [siteId]: {
            ...prevSite,
            bySensor: { ...prevSite.bySensor, [sensorId]: { latest, history } },
          },
        },
      };
    }),

  reset: (siteId) =>
    set((state) => ({
      bySite: {
        ...state.bySite,
        [siteId]: { bySensor: {}, lastUpdatedAt: null },
      },
    })),
}));

function updateState(
  state: RealtimeState,
  siteId: number,
  sensorId: number,
  point: TelemetryPoint,
): Partial<RealtimeState> {
  // Drop buffered/backfilled points (old timestamps flushed after an outage)
  // from the LIVE store entirely — they're persisted server-side and surface in
  // reports; feeding them here makes the current KPI thrash through the backlog.
  const ptMs = new Date(point.time).getTime();
  if (Number.isFinite(ptMs) && Date.now() - ptMs > LIVE_WINDOW_MS) {
    return {};
  }

  const prevSite = state.bySite[siteId] ?? { bySensor: {}, lastUpdatedAt: null };
  const prevSensor = prevSite.bySensor[sensorId] ?? { latest: null, history: [] };
  const history = [...prevSensor.history, point].slice(-HISTORY_LIMIT);
  // Out-of-order guard: never let an older point overwrite a newer live reading.
  const latest =
    prevSensor.latest && prevSensor.latest.time >= point.time ? prevSensor.latest : point;
  return {
    bySite: {
      ...state.bySite,
      [siteId]: {
        bySensor: {
          ...prevSite.bySensor,
          [sensorId]: { latest, history },
        },
        lastUpdatedAt: new Date().toISOString(),
      },
    },
  };
}

// Returns map of sensorId -> { latest, history }
export const useSiteRealtime = (siteId: number) =>
  useRealtimeStore((s) => s.bySite[siteId]);

export const useSensorRealtime = (siteId: number, sensorId: number | null) =>
  useRealtimeStore((s) =>
    sensorId != null ? s.bySite[siteId]?.bySensor[sensorId] : undefined,
  );
